import { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'

import { CREATE_KANBAN_WORKFLOW } from '../../../gql/mutations/kanban'
import { GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { IAssignmentGroupSimple } from '../../../types/assignmentGroups'
import { ICreateKanbanWorkflowRes, IKanbanWorkflow } from '../../../types/kanban'

interface Props {
  show: boolean
  onClose: () => void
  onCreated: (workflow?: IKanbanWorkflow) => void
  availableGroups: IAssignmentGroupSimple[]
  defaultGroupId?: string | null
  canSelectMultipleGroups?: boolean
  userGroups?: { id: string; name: string }[]
  allowedGroups?: IAssignmentGroupSimple[]
}

export const ModalCreateKanbanWorkflow: React.FC<Props> = ({
  show,
  onClose,
  onCreated,
  availableGroups,
  defaultGroupId,
  canSelectMultipleGroups = false,
  userGroups = [],
  allowedGroups,
}) => {
  const intl = useIntl()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<'PRIVATE' | 'GROUP' | 'PUBLIC' | 'SHARED'>('PRIVATE')
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([])
  const [createWorkflow, { loading }] = useMutation<ICreateKanbanWorkflowRes>(CREATE_KANBAN_WORKFLOW)
  const { data: usersData } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    variables: { assignmentCandidates: true },
  })
  const allUsers = usersData?.allUsersList ?? []
  const userGroupMatches = useMemo(() => {
    if (!userGroups.length) return []
    const ids = new Set(userGroups.map((group) => group.id))
    return availableGroups.filter((group) => ids.has(group.id))
  }, [availableGroups, userGroups])
  const defaultGroupMatch = useMemo(() => {
    if (!defaultGroupId || defaultGroupId === 'all') return null
    return availableGroups.find((group) => group.id === defaultGroupId) ?? null
  }, [availableGroups, defaultGroupId])
  const singleGroup = useMemo(() => {
    if (canSelectMultipleGroups) return null
    return userGroupMatches[0] ?? defaultGroupMatch ?? availableGroups[0] ?? null
  }, [availableGroups, canSelectMultipleGroups, defaultGroupMatch, userGroupMatches])

  const multiSelectGroups = useMemo(
    () =>
      canSelectMultipleGroups
        ? allowedGroups && allowedGroups.length > 0
          ? allowedGroups
          : availableGroups
        : [],
    [allowedGroups, availableGroups, canSelectMultipleGroups]
  )
  useEffect(() => {
    if (show) {
      setName('')
      setDescription('')
      setVisibility('PRIVATE')
      setSelectedGroups([])
      setSharedUserIds([])
    }
  }, [show])

  useEffect(() => {
    if (!canSelectMultipleGroups) {
      if (visibility === 'GROUP' && singleGroup?.id) {
        setSelectedGroups([singleGroup.id])
      } else {
        setSelectedGroups([])
      }
    } else if (
      canSelectMultipleGroups &&
      visibility === 'GROUP' &&
      selectedGroups.length === 0 &&
      multiSelectGroups.length > 0
    ) {
      setSelectedGroups([multiSelectGroups[0].id])
    }
  }, [
    canSelectMultipleGroups,
    multiSelectGroups,
    selectedGroups.length,
    singleGroup?.id,
    visibility,
  ])

  const handleClose = () => {
    if (loading) return
    setName('')
    setDescription('')
    setVisibility('PRIVATE')
    setSelectedGroups([])
    setSharedUserIds([])
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const groupAssignmentIds =
      visibility === 'GROUP'
        ? canSelectMultipleGroups
          ? selectedGroups
          : singleGroup?.id
            ? [singleGroup.id]
            : []
        : null
    if (visibility === 'GROUP' && (!groupAssignmentIds || groupAssignmentIds.length === 0)) {
      toast.error(intl.formatMessage({id: 'leads.kanban.selectGroup', defaultMessage: 'Select at least one assignment group'}))
      return
    }
    try {
      const { data } = await createWorkflow({
        variables: {
          name,
          description: description || null,
          visibility,
          groupAssignmentIds: groupAssignmentIds?.length ? groupAssignmentIds : null,
          sharedUserIds: visibility === 'SHARED' ? sharedUserIds : null,
          withDefaultStages: true,
        },
      })
      const payload = data?.createKanbanWorkflow
      if (!payload?.ok || !payload.workflow) {
        toast.error(intl.formatMessage({id: 'leads.kanban.couldNotCreateBoard', defaultMessage: 'The board could not be created.'}))
        return
      }
      toast.success(intl.formatMessage({id: 'leads.kanban.boardCreated', defaultMessage: 'Board created successfully'}))
      handleClose()
      onCreated(payload.workflow)
    } catch (error) {
      console.error(error)
      toast.error(intl.formatMessage({id: 'leads.kanban.errCreatingBoard', defaultMessage: 'Error creating the board'}))
    }
  }

  const handleGroupToggle = (groupId: string) => {
    if (!canSelectMultipleGroups) return
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    )
  }

  const handleToggleSharedUser = (userId: string) => {
    setSharedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }

  return (
    <Modal show={show} onHide={handleClose} centered className='kanban-workflow-modal'>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className='kanban-workflow-modal__header'>
          <Modal.Title><FormattedMessage id='leads.kanban.newBoard' defaultMessage='New Board' /></Modal.Title>
        </Modal.Header>
        <Modal.Body className='kanban-workflow-modal__body'>
          <Form.Group className='kanban-workflow-modal__group'>
            <Form.Label><FormattedMessage id='common.field.name' defaultMessage='Name' /></Form.Label>
            <Form.Control
              className='kanban-workflow-modal__input'
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={intl.formatMessage({id: 'leads.kanban.boardNamePlaceholder', defaultMessage: 'e.g. Leads flow'})}
            />
          </Form.Group>
          <Form.Group className='kanban-workflow-modal__group'>
            <Form.Label><FormattedMessage id='common.field.description' defaultMessage='Description' /></Form.Label>
            <Form.Control
              className='kanban-workflow-modal__input'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={intl.formatMessage({id: 'leads.kanban.optional', defaultMessage: 'Optional'})}
            />
          </Form.Group>
          <Form.Group className='kanban-workflow-modal__group'>
            <Form.Label><FormattedMessage id='leads.kanban.visibility' defaultMessage='Visibility' /></Form.Label>
            <Form.Select
              className='kanban-workflow-modal__input'
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as typeof visibility)}
            >
              <option value='PRIVATE'>{intl.formatMessage({id: 'leads.kanban.visPrivate', defaultMessage: 'Only me'})}</option>
              <option value='GROUP'>{intl.formatMessage({id: 'leads.kanban.visGroup', defaultMessage: 'My assignment group'})}</option>
              <option value='SHARED'>{intl.formatMessage({id: 'leads.kanban.visShared', defaultMessage: 'Shared with specific users'})}</option>
              <option value='PUBLIC'>{intl.formatMessage({id: 'leads.kanban.visPublic', defaultMessage: 'Organization'})}</option>
            </Form.Select>
            {!canSelectMultipleGroups &&
              visibility === 'GROUP' &&
              !singleGroup?.id && (
                <small className='text-muted'>{intl.formatMessage({id: 'leads.kanban.noGroupDetected', defaultMessage: 'No assignment group detected. Ask an administrator to add you to one.'})}</small>
              )}
          </Form.Group>
          {visibility === 'GROUP' && (
            <Form.Group className='kanban-workflow-modal__group'>
              <Form.Label><FormattedMessage id='leads.kanban.assignToGroups' defaultMessage='Assign to groups' /></Form.Label>
              <div className='kanban-workflow-modal__groups'>
                {canSelectMultipleGroups ? (
                  multiSelectGroups.length === 0 ? (
                    <small>{intl.formatMessage({id: 'leads.kanban.noGroupsAvailable', defaultMessage: 'No groups available. Create one first.'})}</small>
                  ) : (
                    multiSelectGroups.map((group) => (
                      <Form.Check
                        key={group.id}
                        type='checkbox'
                        id={`workflow-group-${group.id}`}
                        className='kanban-workflow-modal__checkbox'
                        label={group.name}
                        checked={selectedGroups.includes(group.id)}
                        onChange={() => handleGroupToggle(group.id)}
                      />
                    ))
                  )
                ) : singleGroup?.name ? (
                  <div className='text-muted'>
                    <span className='badge bg-light text-dark me-2 mb-2'>
                      {singleGroup.name}
                    </span>
                  </div>
                ) : (
                  <small className='text-muted'>{intl.formatMessage({id: 'leads.kanban.noGroupForUser', defaultMessage: 'No assignment group detected for your user.'})}</small>
                )}
              </div>
            </Form.Group>
          )}
          {visibility === 'SHARED' && (
            <Form.Group className='kanban-workflow-modal__group'>
              <Form.Label><FormattedMessage id='leads.kanban.selectUsers' defaultMessage='Select users' /></Form.Label>
              <div className='kanban-workflow-modal__users' style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '4px' }}>
                {allUsers.length === 0 ? (
                  <div className='text-muted'>{intl.formatMessage({id: 'leads.kanban.noUsersFound', defaultMessage: 'No users found.'})}</div>
                ) : (
                  allUsers.map((user: any) => (
                    <Form.Check
                      key={user.id}
                      type='checkbox'
                      id={`workflow-user-${user.id}`}
                      className='kanban-workflow-modal__checkbox'
                      label={`${user.firstName} ${user.lastName} (${user.email})`}
                      checked={sharedUserIds.includes(user.id)}
                      onChange={() => handleToggleSharedUser(user.id)}
                    />
                  ))
                )}
              </div>
              <div className='mt-2'>
                <small className='text-muted'>{intl.formatMessage({id: 'leads.kanban.usersSelected', defaultMessage: '{count, plural, one {# user selected} other {# users selected}}'}, {count: sharedUserIds.length})}</small>
              </div>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className='kanban-workflow-modal__footer'>
          <Button variant='light' onClick={handleClose} disabled={loading}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? intl.formatMessage({id: 'leads.kanban.saving', defaultMessage: 'Saving...'}) : intl.formatMessage({id: 'common.action.save', defaultMessage: 'Save'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
